import AsyncStorage from '@react-native-async-storage/async-storage'
import type { RecordModel } from 'pocketbase'
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { Platform } from 'react-native'
import { clearStores, fetchAndSeedUserOrg, pb, preloadStores } from '@/lib/pocketbase'

const IMPERSONATION_STORAGE_KEY = 'jobtimey_impersonation'

interface ImpersonatedUser {
    id: string
    name: string
    email: string
}

interface OriginalAuth {
    token: string
    record: RecordModel
    primaryOrgSlug: string
}

interface ImpersonationData {
    impersonatedUser: ImpersonatedUser
    originalAuth: OriginalAuth
    expiresAt: number
    redirectTo?: string
}

interface ImpersonationContextType {
    isImpersonating: boolean
    impersonatedUser: ImpersonatedUser | null
    originalUser: ImpersonatedUser | null
    stopImpersonation: () => Promise<void>
    isHydrated: boolean
    pendingRedirect: string | null
    clearPendingRedirect: () => void
    refreshFromStorage: () => Promise<void>
}

const ImpersonationContext = createContext<ImpersonationContextType | undefined>(undefined)

interface ImpersonationProviderProps {
    children: ReactNode
}

async function loadImpersonationData(): Promise<ImpersonationData | null> {
    try {
        const data = await AsyncStorage.getItem(IMPERSONATION_STORAGE_KEY)
        if (!data) return null
        return JSON.parse(data)
    } catch {
        return null
    }
}

async function saveImpersonationData(data: ImpersonationData): Promise<void> {
    try {
        await AsyncStorage.setItem(IMPERSONATION_STORAGE_KEY, JSON.stringify(data))
    } catch {
        // Storage might not be available
    }
}

async function clearImpersonationData(): Promise<void> {
    try {
        await AsyncStorage.removeItem(IMPERSONATION_STORAGE_KEY)
    } catch {
        // Storage might not be available
    }
}

function triggerWebRefresh(redirectTo?: string) {
    if (Platform.OS === 'web') {
        if (redirectTo) {
            window.location.href = redirectTo
        } else {
            window.location.reload()
        }
    }
}

export function ImpersonationProvider({ children }: ImpersonationProviderProps) {
    const [isHydrated, setIsHydrated] = useState(false)
    const [impersonationData, setImpersonationData] = useState<ImpersonationData | null>(null)
    const [pendingRedirect, setPendingRedirect] = useState<string | null>(null)

    const refreshFromStorage = useCallback(async () => {
        const data = await loadImpersonationData()
        if (data && data.expiresAt >= Date.now()) {
            setImpersonationData(data)
            if (data.redirectTo) {
                setPendingRedirect(data.redirectTo)
                const updatedData = { ...data, redirectTo: undefined }
                await saveImpersonationData(updatedData)
            }
        }
    }, [])

    useEffect(() => {
        const hydrate = async () => {
            const data = await loadImpersonationData()

            if (data) {
                if (data.expiresAt < Date.now()) {
                    await clearImpersonationData()
                    pb.authStore.save(data.originalAuth.token, data.originalAuth.record)
                    await AsyncStorage.setItem(
                        'jobtimey_primary_org',
                        data.originalAuth.primaryOrgSlug
                    )
                    triggerWebRefresh()
                    return
                }
                setImpersonationData(data)

                if (data.redirectTo) {
                    setPendingRedirect(data.redirectTo)
                    const updatedData = { ...data, redirectTo: undefined }
                    await saveImpersonationData(updatedData)
                }
            }

            setIsHydrated(true)
        }

        hydrate()
    }, [])

    const clearPendingRedirect = useCallback(() => {
        setPendingRedirect(null)
    }, [])

    const stopImpersonation = useCallback(async () => {
        if (!impersonationData) return

        const returnToSlug = impersonationData.originalAuth.primaryOrgSlug

        pb.authStore.save(
            impersonationData.originalAuth.token,
            impersonationData.originalAuth.record
        )
        await AsyncStorage.setItem('jobtimey_primary_org', returnToSlug)
        await clearImpersonationData()

        await clearStores()
        await fetchAndSeedUserOrg()
        await preloadStores()

        setImpersonationData(null)
        setPendingRedirect(`/${returnToSlug}`)

        triggerWebRefresh(`/${returnToSlug}`)
    }, [impersonationData])

    const originalRecord = impersonationData?.originalAuth.record as unknown as
        | { id: string; name: string; email: string }
        | undefined

    const contextValue: ImpersonationContextType = {
        isImpersonating: !!impersonationData,
        impersonatedUser: impersonationData?.impersonatedUser ?? null,
        originalUser: originalRecord
            ? {
                  id: originalRecord.id,
                  name: originalRecord.name,
                  email: originalRecord.email,
              }
            : null,
        stopImpersonation,
        isHydrated,
        pendingRedirect,
        clearPendingRedirect,
        refreshFromStorage,
    }

    return (
        <ImpersonationContext.Provider value={contextValue}>
            {children}
        </ImpersonationContext.Provider>
    )
}

export function useImpersonation(): ImpersonationContextType {
    const context = useContext(ImpersonationContext)
    if (context === undefined) {
        throw new Error('useImpersonation must be used within an ImpersonationProvider')
    }
    return context
}

export async function startImpersonation(
    token: string,
    record: RecordModel,
    originalAuth: OriginalAuth,
    redirectTo?: string
): Promise<void> {
    const userRecord = record as unknown as { id: string; name: string; email: string }
    const impersonationData: ImpersonationData = {
        impersonatedUser: {
            id: userRecord.id,
            name: userRecord.name,
            email: userRecord.email,
        },
        originalAuth,
        expiresAt: Date.now() + 3600 * 1000,
        redirectTo,
    }

    await saveImpersonationData(impersonationData)
    pb.authStore.save(token, record)

    const targetOrgSlug = redirectTo?.replace(/^\//, '')
    if (targetOrgSlug) {
        await AsyncStorage.setItem('jobtimey_primary_org', targetOrgSlug)
    }

    await clearStores()
    await fetchAndSeedUserOrg()
    await preloadStores()

    triggerWebRefresh(redirectTo)
}
